import { useState } from 'react';
import { createConsent } from '../../../../../../api/consent';
import { CONSENT_INTRO, COMMON_SECTIONS, GUARDIAN_CONSENT_NOTICE } from '../consentText/consentContent';
import styles from './GuardianConsentFormStep.module.css';

const RELATION_OPTIONS = ['자녀', '배우자', '형제자매', '기타'];

function GuardianConsentFormStep({ seniorInfo, seniorId, onDone }) {
  const [guardian, setGuardian] = useState({ name: '', phone: '', relation: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [agreed, setAgreed] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleGuardianChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === 'phone') filtered = value.replace(/\D/g, '');
    setGuardian(prev => ({ ...prev, [name]: filtered }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!guardian.name.trim() || guardian.name.trim().length < 2) {
      errs.name = '보호자 이름을 2자 이상 입력해 주세요.';
    }
    if (!/^010\d{8}$/.test(guardian.phone)) {
      errs.phone = '010으로 시작하는 휴대폰번호를 입력해 주세요.';
    }
    if (!guardian.relation) {
      errs.relation = '관계를 선택해 주세요.';
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    if (agreed !== 'agree') {
      setErrorMsg('동의를 선택해 주세요.');
      return;
    }
    setIsLoading(true);
    setErrorMsg('');
    try {
      await createConsent({
        senior: seniorInfo,
        consentType: 'GUARDIAN',
        guardian: { name: guardian.name.trim(), phone: guardian.phone },
        relation: guardian.relation,
      });
      onDone();
    } catch (err) {
      setErrorMsg(err.message || '오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <p className={styles.pageTitle}>개인정보수집 및 이용동의(필수)</p>
      <p className={styles.intro}>{CONSENT_INTRO}</p>

      {COMMON_SECTIONS.map((sec) => (
        <div key={sec.title} className={styles.section}>
          <p className={styles.sectionTitle}>{sec.title}</p>
          <table className={styles.table}>
            <tbody>
              {sec.rows.map((row) => (
                <tr key={row.label}>
                  <th>{row.label}</th>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className={styles.guardianSection}>
        <p className={styles.guardianSectionTitle}>[보호자 동의]</p>

        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>대상자(어르신) 성명</span>
          <span className={styles.infoValue}>{seniorInfo?.name}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>대상자 생년월일</span>
          <span className={styles.infoValue}>{seniorInfo?.birthDate}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>대상자 휴대폰번호</span>
          <span className={styles.infoValue}>{seniorInfo?.phone}</span>
        </div>

        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>보호자 성명</span>
          <input
            className={styles.input}
            name="name"
            value={guardian.name}
            onChange={handleGuardianChange}
            placeholder="보호자 성명"
            maxLength={20}
          />
        </div>
        {fieldErrors.name && <p className={styles.errorMsg}>{fieldErrors.name}</p>}

        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>보호자 휴대폰번호</span>
          <input
            className={styles.input}
            name="phone"
            value={guardian.phone}
            onChange={handleGuardianChange}
            placeholder="01012345678"
            maxLength={11}
            inputMode="numeric"
          />
        </div>
        {fieldErrors.phone && <p className={styles.errorMsg}>{fieldErrors.phone}</p>}

        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>대상자와의 관계</span>
          <select
            className={styles.select}
            name="relation"
            value={guardian.relation}
            onChange={handleGuardianChange}
          >
            <option value="">선택</option>
            {RELATION_OPTIONS.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        {fieldErrors.relation && <p className={styles.errorMsg}>{fieldErrors.relation}</p>}
      </div>

      <p className={styles.notice}>{GUARDIAN_CONSENT_NOTICE}</p>

      <hr className={styles.divider} />

      <div className={styles.agreeRow}>
        <label className={styles.radioLabel}>
          <input
            type="radio"
            name="guardianConsent"
            value="agree"
            checked={agreed === 'agree'}
            onChange={() => { setAgreed('agree'); setErrorMsg(''); }}
          />
          동의
        </label>
        <label className={styles.radioLabel}>
          <input
            type="radio"
            name="guardianConsent"
            value="disagree"
            checked={agreed === 'disagree'}
            onChange={() => setAgreed('disagree')}
          />
          미동의
        </label>
      </div>

      {errorMsg && <p className={styles.fieldErrorMsg}>{errorMsg}</p>}

      <button className={styles.submitBtn} onClick={handleSubmit} disabled={isLoading || agreed !== 'agree'}>
        {isLoading ? '처리 중...' : '제출'}
      </button>
    </div>
  );
}

export default GuardianConsentFormStep;
